/** Every space-level read and write, keyed so one mutation invalidates exactly what it moved. */

import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import type {
  ActivityPage,
  ActivitySummary,
  AddedActivity,
  LoadedSpace,
  MountListFilter,
  MountRecord,
  RequirementBinding,
  SpaceListFilter,
  SpacePage,
  SpaceRecord,
} from "@nebulize/sdk";

import { api } from "@/lib/api";
import { spaces } from "@/lib/spaces";

import type { ActivityVerb } from "../../lib/runState";
import { useConsole } from "../../store";
import type {
  AddedActivityRow,
  CatalogEntry,
  SettingValue,
  SpaceRow,
} from "../../types";

import {
  addedRowOf,
  bindingOf,
  entryOf,
  mountFor,
  spaceRowOf,
} from "./present";
import type { PublishedVersion } from "./present";
import {
  actOnActivity,
  configureSettings,
  moveAddedActivityVersion,
  removeAddedActivity,
  renameAddedActivity,
} from "./verbs";

export const spaceKeys = {
  all: ["spaces"] as const,
  page: (state: SpaceListFilter) => ["spaces", "page", state] as const,
  loaded: ["spaces", "loaded"] as const,
  added: (spaceId: string) => ["spaces", spaceId, "added"] as const,
  mounts: (spaceId: string, state: MountListFilter) =>
    ["spaces", spaceId, "mounts", state] as const,
  bindings: (addedActivityId: string) =>
    ["spaces", "added-activity", addedActivityId, "bindings"] as const,
  catalog: ["activities", "page"] as const,
  version: (activityId: string, version: string) =>
    ["activities", activityId, "versions", version] as const,
};

function pairKey(activityId: string, version: string): string {
  return `${activityId}@${version}`;
}

/** Every space this account holds, hydrated into the store the rest of the console reads. */
export function useSpaceRows(state: SpaceListFilter = "all"): {
  rows: SpaceRow[];
  query: UseQueryResult<SpacePage>;
} {
  const hydrate = useConsole((held) => held.hydrateSpaces);
  const query = useQuery({
    queryKey: spaceKeys.page(state),
    queryFn: () => spaces.list(state),
  });
  const rows = useMemo(
    () => (query.data?.spaces ?? []).map(spaceRowOf),
    [query.data],
  );
  const found = query.isSuccess;

  useEffect(() => {
    if (found) hydrate(rows);
  }, [found, rows, hydrate]);

  return { rows, query };
}

/** 204 is the answer for nothing loaded, so `null` is a state and never a miss. */
/** How often the console re-reads whether a machine is up. The cloud's own window is 90s. */
const PRESENCE_POLL_MS = 15_000;

/**
 * The machines connected to one space, or none while no space is named.
 *
 * Read so the console can say whether the machine is there. The endpoint existed and nothing
 * called it, so the site had no way to show what the other half of the pair was doing.
 */
export function useSpaceAgents(spaceId: string | null | undefined) {
  return useQuery({
    queryKey: [...spaceKeys.all, "agents", spaceId],
    queryFn: () => spaces.agents(spaceId as string),
    enabled: Boolean(spaceId),
    refetchInterval: PRESENCE_POLL_MS,
    retry: false,
  });
}

export function useLoadedSpace(): UseQueryResult<LoadedSpace | null> {
  const hydrate = useConsole((held) => held.hydrateLoaded);
  const query = useQuery({
    queryKey: spaceKeys.loaded,
    queryFn: async () =>
      (await api.get<LoadedSpace | undefined>("/v1/spaces/loaded")) ?? null,
  });
  const loaded = query.data;
  const found = query.isSuccess;

  useEffect(() => {
    if (found) hydrate(loaded ?? null);
  }, [found, loaded, hydrate]);

  return query;
}

export function useMounts(
  spaceId: string,
  state: MountListFilter = "live",
): UseQueryResult<MountRecord[]> {
  return useQuery({
    queryKey: spaceKeys.mounts(spaceId, state),
    enabled: spaceId !== "",
    queryFn: async () => (await spaces.listMounts(spaceId, state)).mounts ?? [],
  });
}

export interface SpaceActivities {
  rows: AddedActivityRow[];
  entryFor: (row: AddedActivityRow) => CatalogEntry | undefined;
  pending: boolean;
  /** True while any pinned manifest is still loading, so a reader never mistakes loading for underived. */
  manifestsPending: boolean;
  /** False until the mount read lands, so nothing claims what is running on a read that failed. */
  liveRead: boolean;
}

/** What is added to one space, each row carrying the mount it holds and the manifest it pins. */
export function useSpaceActivities(spaceId: string): SpaceActivities {
  const hydrate = useConsole((held) => held.hydrateAdded);

  const added = useQuery({
    queryKey: spaceKeys.added(spaceId),
    enabled: spaceId !== "",
    queryFn: () =>
      api.get<AddedActivity[]>(`/v1/spaces/${spaceId}/added-activities`),
  });
  const mounts = useMounts(spaceId, "live");

  const configured = useMemo(() => added.data ?? [], [added.data]);

  const bindings = useQueries({
    queries: configured.map((one) => ({
      queryKey: spaceKeys.bindings(one.added_activity_id),
      queryFn: () =>
        api.get<RequirementBinding[]>(
          `/v1/added-activities/${one.added_activity_id}/bindings`,
        ),
    })),
  });

  const pinned = useMemo(() => {
    const seen = new Map<string, { activity_id: string; version: string }>();
    for (const one of configured) {
      seen.set(pairKey(one.activity_id, one.activity_version), {
        activity_id: one.activity_id,
        version: one.activity_version,
      });
    }
    return [...seen.values()];
  }, [configured]);

  const versions = useQueries({
    queries: pinned.map((one) => ({
      queryKey: spaceKeys.version(one.activity_id, one.version),
      queryFn: () =>
        api.get<PublishedVersion>(
          `/v1/activities/${one.activity_id}/versions/${one.version}`,
        ),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const entries = useMemo(() => {
    const out = new Map<string, CatalogEntry>();
    for (const one of versions) {
      if (one.data === undefined) continue;
      out.set(
        pairKey(one.data.activity_id, one.data.version),
        entryOf(one.data),
      );
    }
    return out;
  }, [versions]);

  const live = mounts.data ?? [];
  const rows = configured.map((one, index) =>
    addedRowOf(
      one,
      mountFor(one.added_activity_id, live),
      (bindings[index]?.data ?? []).map(bindingOf),
    ),
  );

  const found = added.isSuccess;
  useEffect(() => {
    if (found && spaceId !== "") hydrate(spaceId, rows);
  }, [found, spaceId, rows, hydrate]);

  return {
    rows,
    entryFor: (row) =>
      entries.get(pairKey(row.activity_id, row.activity_version)),
    pending: added.isPending || mounts.isPending,
    manifestsPending: versions.some((one) => one.isPending),
    liveRead: mounts.isSuccess,
  };
}

/** The catalog, one row per activity, newest version of each. */
export function useCatalog(): UseQueryResult<ActivitySummary[]> {
  return useQuery({
    queryKey: spaceKeys.catalog,
    queryFn: async () =>
      (await api.get<ActivityPage>("/v1/activities?limit=100")).activities ??
      [],
    staleTime: 5 * 60 * 1000,
  });
}

function useSpaceInvalidation(): () => void {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: spaceKeys.all });
  };
}

/** Durable configuration, not yet live. Starting it is the row's own Start. */
export function useAddActivity(
  spaceId: string,
): UseMutationResult<AddedActivity, Error, ActivitySummary> {
  const invalidate = useSpaceInvalidation();
  return useMutation({
    mutationFn: (entry: ActivitySummary) =>
      spaces.addActivity(spaceId, {
        activity_id: entry.activity_id,
        activity_version: entry.version,
      }),
    onSuccess: invalidate,
  });
}

export interface ActivityAct {
  row: AddedActivityRow;
  verb: ActivityVerb;
}

export function useActivityVerb(): UseMutationResult<void, Error, ActivityAct> {
  const invalidate = useSpaceInvalidation();
  return useMutation({
    mutationFn: ({ row, verb }: ActivityAct) => actOnActivity(row, verb),
    onSuccess: invalidate,
  });
}

export interface SettingEdit {
  key: string;
  value: SettingValue;
}

export function useSetting(
  addedActivityId: string,
): UseMutationResult<unknown, Error, SettingEdit> {
  const invalidate = useSpaceInvalidation();
  return useMutation({
    mutationFn: ({ key, value }: SettingEdit) =>
      configureSettings(addedActivityId, { [key]: value }),
    onSuccess: invalidate,
  });
}

export function useRenameActivity(
  addedActivityId: string,
): UseMutationResult<unknown, Error, string> {
  const invalidate = useSpaceInvalidation();
  return useMutation({
    mutationFn: (name: string) => renameAddedActivity(addedActivityId, name),
    onSuccess: invalidate,
  });
}

export interface VersionMove {
  version: string;
  dropUnsupported: boolean;
}

export function useMoveVersion(
  addedActivityId: string,
): UseMutationResult<unknown, Error, VersionMove> {
  const invalidate = useSpaceInvalidation();
  return useMutation({
    mutationFn: ({ version, dropUnsupported }: VersionMove) =>
      moveAddedActivityVersion(addedActivityId, version, dropUnsupported),
    onSuccess: invalidate,
  });
}

export function useRemoveActivity(): UseMutationResult<
  undefined,
  Error,
  string
> {
  const invalidate = useSpaceInvalidation();
  return useMutation({
    mutationFn: (addedActivityId: string) =>
      removeAddedActivity(addedActivityId),
    onSuccess: invalidate,
  });
}

/** A space, made. The one control that creates one is the Spaces page's own New space. */
export function useCreateSpace(): UseMutationResult<SpaceRecord, Error, string> {
  const invalidate = useSpaceInvalidation();
  return useMutation({
    mutationFn: (name: string) => spaces.create(name),
    onSuccess: invalidate,
  });
}
